import { z } from 'zod';
import { paginationQuerySchema } from '../common/pagination.js';
import { checkFloorRack, checkRackBayWidths, checkRackRows, rackGeometryBase, rackKindSchema, } from './geometry.js';
import { rackCodeSchema, zoneCodeSchema } from './cell-code.js';
const idRef = z.string().regex(/^\d+$/, 'Identifikator raqamlardan iborat bo\'lishi kerak');
export const warehouseQuerySchema = paginationQuerySchema.extend({
    mtuId: idRef.optional(),
    orgUnitId: idRef.optional(),
    search: z.string().max(200).optional(),
});
export const createWarehouseSchema = z.object({
    orgUnitId: idRef,
    code: z.string().min(3).max(20),
    name: z.string().min(3).max(200),
    address: z.string().max(300).optional(),
    dims: z.object({
        lengthM: z.number().positive().max(1000),
        widthM: z.number().positive().max(1000),
        heightM: z.number().positive().max(100),
    }),
    /**
     * OCHIQ MAYDON — tomi va devori yo'q joy: hovli, estakada yoni,
     * konteyner maydonchasi.
     *
     * Saqlashga ta'siri YO'Q va bo'lishi ham kerak emas: u yerda ham
     * zona bor, maydon bor, katak bor, etiketka bor. Farq ko'rinishda:
     * hovli ustiga devor chizilsa, 3D reja ombordagi narsaga umuman
     * o'xshamay qolardi.
     */
    isOutdoor: z.boolean().default(false),
});
/** Kod o'zgartirilmaydi: u etiketkalarga bosilgan va hujjatlarda uchraydi. */
export const updateWarehouseSchema = z.object({
    name: z.string().min(3).max(200).optional(),
    address: z.string().max(300).nullable().optional(),
    dims: z
        .object({
        lengthM: z.number().positive().max(1000),
        widthM: z.number().positive().max(1000),
        heightM: z.number().positive().max(100),
    })
        .optional(),
    isActive: z.boolean().optional(),
    isOutdoor: z.boolean().optional(),
});
export const createZoneSchema = z.object({
    warehouseId: idRef,
    code: zoneCodeSchema,
    name: z.string().min(2).max(100),
    color: z.string().regex(/^#[0-9a-f]{6}$/i, 'Rang #RRGGBB formatida bo\'lishi kerak'),
    plan: z.object({
        x: z.number().finite(),
        y: z.number().finite(),
        w: z.number().positive(),
        d: z.number().positive(),
    }),
});
export const updateZoneSchema = z.object({
    name: z.string().min(2).max(100).optional(),
    color: z.string().regex(/^#[0-9a-f]{6}$/i, 'Rang #RRGGBB formatida bo\'lishi kerak').optional(),
    plan: z
        .object({
        x: z.number().finite(),
        y: z.number().finite(),
        w: z.number().positive(),
        d: z.number().positive(),
    })
        .optional(),
});
export const createRackSchema = z
    .object({
    zoneId: idRef,
    code: rackCodeSchema,
    name: z.string().min(2).max(150).optional(),
    /*
     * TUR faqat YARATISHDA beriladi va keyin o'zgarmaydi.
     *
     * Jimlikdagi qiymat bor, chunki maydon keyin qo'shildi: turini
     * aytmagan chaqiruv ilgarigidek temir stellaj yasaydi.
     */
    kind: rackKindSchema.default('RACK'),
})
    .extend(rackGeometryBase.shape)
    .superRefine((rack, ctx) => {
    checkRackRows(rack, ctx);
    checkFloorRack(rack, ctx);
    /* Tur FAQAT shu yerda ma'lum. Tahrirda u yuborilmaydi va o'sha
       qoidani xizmat qatlami saqlangan tur bo'yicha qo'yadi. */
    checkRackBayWidths(rack, ctx);
});
/**
 * Stellaj kodi tahrirda YO'Q: kod katak kodining bir qismi, katak kodi esa
 * javonga yopishtirilgan etiketkada turadi. `bays`/`levelHeights`
 * o'zgarganda faqat haqiqatan qo'shilgan/olib tashlangan kataklar tegiladi.
 */
export const updateRackSchema = z
    .object({
    name: z.string().min(2).max(150).nullable().optional(),
    pos: rackGeometryBase.shape.pos.optional(),
    depthM: rackGeometryBase.shape.depthM.optional(),
    bays: rackGeometryBase.shape.bays.optional(),
    levelHeights: rackGeometryBase.shape.levelHeights.optional(),
})
    /*
     * `bays` yolg'iz kelsa qatorlar soni tekshirilmaydi — bu ATAYLAB:
     * PATCH da tegilmagan maydon "o'zgarmadi" degani va uni bu yerda
     * bilib bo'lmaydi. Mosligini xizmat qatlami tekshiradi, u yerda
     * javonning hozirgi holati bor.
     */
    .superRefine(checkRackRows);
/**
 * Katak holati. Ro'yxat SHARTNOMADA turadi, chunki uni frontend ham
 * varaqlaydi (tanlagich) — takrorlangan ro'yxat bir kun kelib serverdagidan
 * farq qilib qolardi.
 */
export const CELL_STATUSES = ['FREE', 'OCCUPIED', 'BLOCKED'];
export const cellStatusSchema = z.enum(CELL_STATUSES);
/**
 * Bitta katakka ajratiladigan mahsulotlar CHEGARASI.
 *
 * Chegara kerak, chunki ro'yxat so'rovdan keladi: chegarasiz maydonga
 * o'n minglab identifikator yuborib, har biriga bittadan qator yozdirish
 * mumkin bo'lardi. Ellik — javonning bir o'rniga sig'adigan turli
 * mahsulot sonidan ancha yuqori, ya'ni haqiqiy ishga to'siq emas.
 */
export const MAX_CELL_RESERVATIONS = 50;
export const updateCellSchema = z.object({
    status: cellStatusSchema.optional(),
    capacityQty: z.number().nonnegative().nullable().optional(),
    capacityWeightKg: z.number().nonnegative().nullable().optional(),
    note: z.string().max(500).nullable().optional(),
    /**
     * Katakni MAHSULOT uchun ajratish. Bo'sh ro'yxat — ajratishni bekor
     * qilish, maydonning o'zi berilmasa ajratish TEGILMAYDI.
     *
     * RO'YXAT, bitta identifikator emas: bitta o'ringa bir nechta mahsulot
     * qo'yish omborda odatiy hol — bir o'lchamdagi boltlar yonma-yon
     * turadi va ularni ikki o'ringa ajratish javonda joy isrof qiladi.
     * Ilgari bu ustun bitta bo'lgani uchun omborchi ikkinchi mahsulotni
     * ajratganda birinchisi jimgina o'chib ketardi.
     *
     * Alohida manzil ATAYLAB QILINMADI: ajratish katakning xossasi, xuddi
     * sig'im yoki izoh kabi, va u ham `warehouse.write` talab qiladi.
     * Ikkinchi endpoint ikkinchi ruxsat tekshiruvi va ikkinchi audit
     * yozuvi degani bo'lardi — bir xil ish uchun ikki yo'l.
     *
     * Takrorlar rad etiladi: bir mahsulot ikki marta ajratilishi ma'nosiz
     * va u faqat interfeys xatosidan kelib chiqadi.
     */
    reservedNomenclatureIds: z
        .array(idRef)
        .max(MAX_CELL_RESERVATIONS)
        .refine((ids) => new Set(ids).size === ids.length, 'Mahsulot ikki marta ajratilgan')
        .optional(),
});
/**
 * KATAKLARNI BIRLASHTIRISH — javondagi ajratgich olib tashlandi.
 *
 * Javonni birinchi marta to'g'ri bo'lish kam uchraydi: usta ikkita tor
 * o'rin yasaydi, keyin ma'lum bo'ladiki, u yerga turadigan narsa bitta
 * va kengroq. Fizik ish oddiy — ajratgich chiqarib olinadi.
 *
 * Chegara o'n ikkita: bitta qatorda bundan ko'p o'rinni birdan qo'shish
 * javonni qayta yasash degani va u ustadan o'tishi kerak.
 */
export const MAX_MERGE_CELLS = 12;
export const mergeCellsSchema = z.object({
    cellIds: z
        .array(idRef)
        .min(2, 'Kamida ikkita katak tanlanishi kerak')
        .max(MAX_MERGE_CELLS)
        .refine((ids) => new Set(ids).size === ids.length, 'Katak ikki marta tanlangan'),
});
/**
 * Katak ajratilgan mahsulot — fasadda nomi, kirim satrida kodi ham kerak.
 */
export const reservedItemSchema = z.object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
});
/** Skaner yo'li: katak kodi ombor ichida unikal, shuning uchun ombor majburiy. */
export const cellByCodeQuerySchema = z.object({ warehouseId: idRef });
/**
 * SKANER JAVOBI — katak va uning to'liq manzili.
 *
 * `by-code` dan farqi ataylab: u kirim sahifasidagi tanlagichning issiq
 * yo'li va faqat katakning o'zini qaytaradi. Skaner esa javon oldida
 * turgan odamga ekran to'ldirib beradi — zona nomi, stellaj nomi, qator va
 * o'rin. Ikkalasini bitta javobga tiqish tanlagichning har bosishida
 * keraksiz `JOIN` degani bo'lardi.
 */
export const cellCardSchema = z.object({
    id: z.string(),
    rackId: z.string(),
    warehouseId: z.string(),
    warehouseName: z.string(),
    zoneCode: z.string(),
    zoneName: z.string(),
    rackCode: z.string(),
    rackName: z.string().nullable(),
    code: z.string(),
    level: z.number(),
    idx: z.number(),
    status: cellStatusSchema,
    capacityQty: z.number().nullable(),
    capacityWeightKg: z.number().nullable(),
    note: z.string().nullable(),
    /**
     * Katak SHU mahsulotlar uchun ajratilgan. Bo'sh ro'yxat — umumiy katak.
     *
     * Kod ham, nom ham keladi: kartochka nomni ko'rsatadi, kirim satridagi
     * ogohlantirish esa qaysi mahsulot ekanini aniq aytishi kerak.
     */
    reserved: z.array(reservedItemSchema),
});
/**
 * Skaner natijasi RO'YXAT, bitta katak emas.
 *
 * Katak kodi bitta ombor ichida unikal, lekin ikki omborda bir xil `A-01-01-01`
 * bo'lishi mumkin. Skaner qaysi omborda turganini bilmaydi, shuning uchun
 * server taxmin qilmaydi: bittadan ko'p topilsa, tanlashni odamga qoldiradi.
 */
export const cellScanResultSchema = z.object({ matches: z.array(cellCardSchema) });
/** Skanerlangan kodni ombor bilan toraytirish — ixtiyoriy. */
export const cellScanQuerySchema = z.object({ warehouseId: idRef.optional() });
//# sourceMappingURL=warehouse.schema.js.map